import type { HeavyArtifactResponse, PickerArtifact } from "@/types/artifacts.ts";
import type { ReactElement } from "react";
import {
  computerDesktopClosePath,
  computerDesktopOpenPath,
  computerDesktopPastePath,
} from "@/shared/computerDesktop.ts";
import { ComputerDesktopOmniBody } from "./ComputerDesktopOmniBody.tsx";
import { CalendarsOmniPickerBody } from "./CalendarsOmniPickerBody.tsx";
import { ConnectorsOmniPickerBody } from "./ConnectorsOmniPickerBody.tsx";
import { LocationsOmniPickerBody } from "./LocationsOmniPickerBody.tsx";
import { ProtectedMeetingsOmniPickerBody } from "./ProtectedMeetingsOmniPickerBody.tsx";
import { RoutinesOmniPickerBody } from "./RoutinesOmniPickerBody.tsx";
import { SlackChannelsOmniPickerBody } from "./SlackChannelsOmniPickerBody.tsx";

/**
 * The bespoke body an artifact gets inside the omnibox card, if it has one.
 *
 * `ArtifactBiscuit` draws the same chrome around every heavy artifact — pager,
 * prompt, numbered action rows, follow-up box — and fills the middle with
 * the server-built summary: a title and an excerpt naming a few of the rows. For
 * an artifact whose whole point is a decision the user makes *in* it, or a
 * single action like opening the computer, that summary describes the card
 * instead of being it, and the only way to act is to leave for the canvas.
 *
 * So the middle is pluggable. Returning a body here replaces the summary card
 * and nothing else; everything around it stays shared, which is what keeps the
 * set of these cheap to grow. A kind with no entry falls through to the summary,
 * so nothing has to be written before it's wanted.
 *
 * `threadId` is the thread the card is shown in, for a body that refreshes its
 * feed once the card is done.
 */
export const omniArtifactBody = (
  artifact: HeavyArtifactResponse,
  threadId: number,
): ReactElement | undefined => {
  switch (artifact.artifactType) {
    case "picker":
      return pickerOmniBody(artifact);
    // The card exists to be opened, so the button is the body. Without a route
    // there is no button, and the summary stands in.
    case "computerDesktop": {
      const openPath = computerDesktopOpenPath(artifact);
      return openPath ? (
        <ComputerDesktopOmniBody
          openPath={openPath}
          closePath={computerDesktopClosePath(artifact)}
          pastePath={computerDesktopPastePath(artifact)}
          threadId={threadId}
        />
      ) : undefined;
    }
    default:
      return undefined;
  }
};

/**
 * One picker body per kind, dispatched exactly like the canvas's `PickerBlock`.
 *
 * A switch rather than a lookup table so each kind narrows its own `target` and
 * takes only the framing it needs — the connectors kind has scoping and ranking
 * to read, calendars has none.
 *
 * The `default` is a real fallback, not an exhaustiveness hole: a kind with no
 * case here gets the server-built summary, which is a coherent card rather than
 * a broken one. That is what lets each kind's body be written when it's worth
 * designing instead of all at once. Adding one is a `case` and nothing else.
 */
const pickerOmniBody = (picker: PickerArtifact): ReactElement | undefined => {
  switch (picker.target.kind) {
    case "connectors":
      // The one kind with framing to honour: which services to offer, and which
      // of them to put first. Read straight off the narrowed target — that is
      // what the switch is for.
      return (
        <ConnectorsOmniPickerBody
          scopeIds={picker.target.scopeIds}
          pinnedIds={picker.target.pinnedIds}
          preferredIds={picker.target.preferredIds}
        />
      );
    case "calendars":
      return <CalendarsOmniPickerBody />;
    case "locations":
      return <LocationsOmniPickerBody />;
    case "protectedMeetings":
      return <ProtectedMeetingsOmniPickerBody />;
    case "routines":
      return <RoutinesOmniPickerBody />;
    case "slackChannels":
      return <SlackChannelsOmniPickerBody picker={picker} teamId={picker.target.teamId} />;
    default:
      return undefined;
  }
};
